import axios from 'axios';

export interface UserProfile {
  id: string;
  name: string | null;
  email: string;
  image: string | null;
  company: string | null;
  website: string | null;
  emailVerified: Date | null;
  twoFactorEnabled: boolean;
}

export interface UpdateProfileData {
  name?: string;
  image?: string;
  company?: string;
  website?: string;
}

export interface ProfileResponse {
  success: boolean;
  data: UserProfile;
}

/**
 * Get current user's profile
 * @returns Promise with user profile data
 */
export const getProfileApi = async (): Promise<UserProfile> => {
  const response = await axios.get<ProfileResponse>('/api/user/profile');
  return response.data.data;
};

/**
 * Update current user's profile
 * @param data - Profile data to update
 * @returns Promise with updated user profile data
 */
export const updateProfileApi = async (
  data: UpdateProfileData,
): Promise<UserProfile> => {
  const response = await axios.patch<ProfileResponse>(
    '/api/user/profile',
    data,
  );
  return response.data.data;
};

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
}

export interface ChangePasswordResponse {
  success: boolean;
  message: string;
}

/**
 * Change user password
 * @param data - Password change data
 * @returns Promise with success response
 */
export const changePasswordApi = async (
  data: ChangePasswordData,
): Promise<ChangePasswordResponse> => {
  const response = await axios.post<ChangePasswordResponse>(
    '/api/user/change-password',
    data,
  );
  return response.data;
};

export interface ConnectedAccount {
  id: string;
  provider: string;
  type: string;
  providerAccountId: string;
  createdAt: Date;
}

export interface AccountsResponse {
  success: boolean;
  data: ConnectedAccount[];
}

/**
 * Get current user's connected accounts
 * @returns Promise with connected accounts
 */
export const getConnectedAccountsApi = async (): Promise<
  ConnectedAccount[]
> => {
  const response = await axios.get<AccountsResponse>('/api/user/accounts');
  return response.data.data;
};

export interface ActiveSession {
  id: string;
  device: string;
  location: string;
  ip: string;
  lastActive: string;
  current: boolean;
  expires: string;
}

export interface SessionsResponse {
  success: boolean;
  data: ActiveSession[];
}

/**
 * Get current user's active sessions
 * @returns Promise with active sessions
 */
export const getActiveSessionsApi = async (): Promise<ActiveSession[]> => {
  const response = await axios.get<SessionsResponse>('/api/user/sessions');
  return response.data.data;
};

/**
 * Revoke a session
 * @param sessionId - Session ID to revoke
 * @returns Promise with success response
 */
export const revokeSessionApi = async (sessionId: string): Promise<void> => {
  await axios.delete(`/api/user/sessions?sessionId=${sessionId}`);
};

export interface TwoFactorSetupResponse {
  success: boolean;
  data: {
    secret: string;
    qrCodeUrl: string;
  };
}

/**
 * Setup 2FA (generate secret and QR code)
 * @returns Promise with 2FA setup data
 */
export const setup2FAApi = async (): Promise<
  TwoFactorSetupResponse['data']
> => {
  const response = await axios.post<TwoFactorSetupResponse>(
    '/api/user/two-factor/setup',
  );
  return response.data.data;
};

export interface Verify2FAData {
  code: string;
}

export interface Verify2FAResponse {
  success: boolean;
  message: string;
}

/**
 * Verify 2FA code and enable 2FA
 * @param data - Verification code
 * @returns Promise with success response
 */
export const verify2FAApi = async (
  data: Verify2FAData,
): Promise<Verify2FAResponse> => {
  const response = await axios.post<Verify2FAResponse>(
    '/api/user/two-factor/verify',
    data,
  );
  return response.data;
};

export interface Disable2FAResponse {
  success: boolean;
  message: string;
}

/**
 * Disable 2FA
 * @returns Promise with success response
 */
export const disable2FAApi = async (): Promise<Disable2FAResponse> => {
  const response = await axios.post<Disable2FAResponse>(
    '/api/user/two-factor/disable',
  );
  return response.data;
};
